<template>
  <v-dialog
    v-model="dialogValue"
    max-width="600"
    height="600"
    persistent
    overlay-color="grey darken-1"
    :overlay-opacity="1"
  >
    <v-card class="rounded-lg">
      <v-card-title class="font-weight-medium">
        Plan Summary And Add-Ons
        <v-spacer />
        <v-btn
          icon
          @click="dialogValue = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-8">
        <template>
          <p
            class="text-body-1 px-0 font-weight-medium"
          >
            Your Plan
          </p>
          <p class="text-caption grey--text text--darken-3 px-0 mt-n4">
            {{ planTitle }}
          </p>
        </template>

        <template v-for="addon in addonEntries">
          <v-row
            :key="addon.id"
            class="flex-nowrap px-3 justify-space-between align-center my-4"
          >
            <span
              class="text-body-1"
              :class="{'font-weight-bold' : addon.added }"
            >
              {{ addon.text }}
            </span>
            <div class="d-flex align-center g-4">
              <template>
                <div>
                  <animated-number
                    class="text-right text-body-1 grey--text text--darken-4"
                    prefix="$"
                    :number="addon.price"
                  />
                  <span class="text-caption">/{{ frequency }}</span>
                  <span
                    class="success--text text-caption"
                  >*</span>
                </div>
              </template>
              <v-btn
                icon
                class="button grey"
                :class="{ 'primary': !addon.added, 'lighten-3': addon.added }"
                :color="!addon.added ? 'white' : 'grey--text'"
                @click="toggle(addon.id)"
              >
                <v-icon>
                  {{ addon.added ? 'mdi-minus' : 'mdi-plus' }}
                </v-icon>
              </v-btn>
            </div>
          </v-row>
          <p
            :key="`${addon.id}-description`"
            class="text-caption grey--text text--darken-1"
          >
            {{ addon.description }}
          </p>
        </template>

        <v-divider class="my-4" />
        <v-row class="flex-nowrap px-3 justify-space-between my-2">
          <span class="font-weight-medium primary-grey--text text-body-1 px-0">
            Total
          </span>
          <span
            class="text-right text-h6 primary--text font-weight-regular pl-0"
          >
            <animated-number
              :key="total"
              prefix="$"
              :number="total"
            />
            <span class="text-caption">/{{ frequency }}</span>
            <span
              class="success--text text-caption"
            >*</span>
          </span>
        </v-row>
        <v-row
          class="flex-nowrap px-3 my-2 success--text"
        >
          <span class="text-caption">*</span>
          <span class="">Any coupons or discounts will be applied at checkout.</span>
        </v-row>
      </v-card-text>

      <v-card-actions class="d-flex justify-end">
        <v-btn
          text
          @click="dialogValue = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onContinue"
        >
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AnimatedNumber from '@/components/generic/AnimatedNumber.vue';
import CATALOG from './data/_catalog';

export default {
  name: 'AddonModal',

  components: {
    AnimatedNumber,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },

    plan: {
      type: Object,
      default: null,
    },

    billingCycle: {
      type: String,
      default: 'annual',
    },
  },

  data() {
    return {
      addons: { 'Weekly-Research': false },
      region: 'US',
    };
  },

  computed: {
    catalogAddons() {
      return (CATALOG[this.region] && CATALOG[this.region].addons) || [];
    },

    dialogValue: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit('input', newValue);
      },
    },

    annualBilling() {
      return this.billingCycle === 'annual';
    },

    frequency() {
      return this.annualBilling ? 'year' : 'month';
    },

    planTitle() {
      return this.plan?.title;
    },

    selectedAddons() {
      return Object.entries(this.addons)
        .filter(([, value]) => value)
        .map(([key]) => key);
    },

    addonEntries() {
      const us = this.catalogAddons.find((a) => a.id === 'Weekly-Research');
      return [
        {
          id: us?.id || 'Weekly-Research',
          added: this.isAdded(us?.id || 'Weekly-Research'),
          text: us?.title || 'Add Weekly Research',
          description: us?.description || 'The Weekly Research provided by AgResource…',
          price: this.addonPrice(us?.id || 'Weekly-Research'),
        },
      ];
    },

    total() {
      const addonsSum = this.selectedAddons
        .map((addonId) => this.addonPrice(addonId))
        .reduce((a, b) => a + b, 0);

      const planPart = this.annualBilling
        ? this.plan?.priceAnnual || 0
        : this.plan?.priceMonthly || 0;

      return addonsSum + planPart;
    },
  },

  methods: {
    findAddonInCatalog(addonId) {
      return this.catalogAddons.find((a) => a.id === addonId) || null;
    },

    toggle(addon) {
      if (this.addons[addon]) {
        this.$set(this.addons, addon, false);
        return;
      }
      Object.keys(this.addons).forEach((k) => this.$set(this.addons, k, false));
      this.$set(this.addons, addon, true);
    },

    isAdded(addon) {
      return Boolean(this.addons[addon]);
    },

    addonPrice(addon) {
      const entry = this.findAddonInCatalog(addon);
      if (!entry) return 0;

      return this.annualBilling
        ? (entry.priceAnnual ?? 0)
        : (entry.priceMonthly ?? 0);
    },

    onContinue() {
      const freq = this.annualBilling ? 'Yearly' : 'Monthly';

      const selectedAddonDetails = this.addonEntries
        .filter((addon) => this.selectedAddons.includes(addon.id))
        .map((addon) => {
          const addonId = `${addon.id}-USD-${freq}`;
          return {
            id: addonId,
            price: addon.price,
          };
        });

      this.$emit('continue', selectedAddonDetails);
    },
  },
};
</script>
